import { GoogleGenAI } from '@google/genai';
import type { SlotScreenId, VideoCompilationConfig, VideoSlotImage } from '@core/types/slot-screen-generator.types';
import { SLOT_SCREEN_ORDER } from '@core/types/slot-screen-generator.types';
import { DEFAULT_SCREEN_CONFIGS } from '../../constants/slot-screen-defaults';
import { useSlotAssetGeneratorStore } from '@store/slotAssetGeneratorStore';
import { resolveApiKey } from '@utils/api-keys';
import { shouldUseProxy, proxyStartVideoGeneration, proxyPollVideoStatus } from '../ai-proxy';
import { refineAnimationPrompt } from './AnimationPromptReasoningService';

// ─── Constants ───────────────────────────────────────────

const VEO_MODEL = 'veo-3.1-generate-preview';
const POLL_INTERVAL_MS = 10_000;
const MAX_POLL_ATTEMPTS = 60; // 10 minutes

// ─── Helpers ──────────────────────────────────────────────

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]); // strip data:...;base64, prefix
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function buildSegments(
  screenImages: Record<string, string>,
  config: VideoCompilationConfig
): Array<{ screenId: SlotScreenId; durationMs: number }> {
  const segments: Array<{ screenId: SlotScreenId; durationMs: number }> = [];
  for (const screenId of SLOT_SCREEN_ORDER) {
    if (screenImages[screenId]) {
      const durationMs = (config.screenDurations[screenId] || 3) * 1000;
      segments.push({ screenId, durationMs });
    }
  }
  return segments;
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new Error('Aborted')); return; }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => { clearTimeout(timer); reject(new Error('Aborted')); }, { once: true });
  });
}

// ─── Local WebM Compilation (Canvas + MediaRecorder) ──────

async function compileLocalWebM(
  screenImages: Record<string, string>,
  config: VideoCompilationConfig,
  onProgress: (pct: number) => void,
  signal?: AbortSignal
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = config.width;
  canvas.height = config.height;
  const ctx = canvas.getContext('2d')!;

  const segments = buildSegments(screenImages, config);
  if (segments.length === 0) throw new Error('No screen images selected for compilation');

  const totalDurationMs = segments.reduce((sum, s) => sum + s.durationMs, 0);

  // Pre-load images
  const images: Record<string, HTMLImageElement> = {};
  for (const seg of segments) {
    images[seg.screenId] = await loadImage(screenImages[seg.screenId]);
  }

  // MediaRecorder setup with codec fallback
  const stream = canvas.captureStream(config.fps);
  let mimeType = 'video/webm;codecs=vp9';
  if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm;codecs=vp8';
  if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';

  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5_000_000 });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
  recorder.start(100);

  let elapsedMs = 0;
  const frameInterval = 1000 / config.fps;

  for (const seg of segments) {
    if (signal?.aborted) { recorder.stop(); throw new Error('Aborted'); }
    const img = images[seg.screenId];
    const frameCount = Math.ceil(seg.durationMs / frameInterval);

    for (let f = 0; f < frameCount; f++) {
      if (signal?.aborted) { recorder.stop(); throw new Error('Aborted'); }
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      await new Promise((r) => setTimeout(r, frameInterval));
      elapsedMs += frameInterval;
      onProgress(Math.min(99, Math.round((elapsedMs / totalDurationMs) * 100)));
    }
  }

  recorder.stop();
  await new Promise<void>((resolve) => { recorder.onstop = () => resolve(); });
  onProgress(100);
  return new Blob(chunks, { type: mimeType });
}

// ─── Veo Single Clip Generation (via @google/genai SDK) ──
//
// Each request generates ONE 8-second clip from a single image + prompt.
// The SDK handles image-to-video properly (uploads via Files API internally).
// To make a multi-screen video, we generate clips per-screen then stitch locally.

/**
 * Generate a single Veo video clip.
 *
 * When the backend proxy is available, routes through it so the API key
 * stays server-side. Falls back to direct @google/genai SDK calls when
 * the user provides their own key.
 *
 * Returns a video Blob (MP4).
 */
async function generateVeoClip(
  apiKey: string | null,
  prompt: string,
  imageBase64: string | null,
  imageMimeType: string,
  aspectRatio: '16:9' | '9:16',
  signal?: AbortSignal,
): Promise<Blob> {
  const image = imageBase64
    ? { imageBytes: imageBase64, mimeType: imageMimeType }
    : undefined;

  if (shouldUseProxy()) {
    // ── Secure path: route through backend proxy ──
    const operationName = await proxyStartVideoGeneration(
      {
        model: VEO_MODEL,
        prompt,
        image,
        config: { aspectRatio },
      },
      signal,
    );

    // Poll until done
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      if (signal?.aborted) throw new Error('Aborted');
      await delay(POLL_INTERVAL_MS, signal);

      const status = await proxyPollVideoStatus(operationName, signal);
      if (status.status === 'completed') {
        return status.videoBlob;
      }
      if (status.status === 'failed') {
        throw new Error(status.error || 'Video generation failed');
      }
      // status === 'pending' → continue polling
    }

    throw new Error('Veo video generation timed out after 10 minutes');
  }

  // ── Fallback: direct SDK call with user's own API key ──
  if (!apiKey) {
    throw new Error('Google API key required for video generation.');
  }

  const ai = new GoogleGenAI({ apiKey });

  let operation = await ai.models.generateVideos({
    model: VEO_MODEL,
    prompt,
    image,
    config: { aspectRatio },
  });

  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    if (signal?.aborted) throw new Error('Aborted');
    if (operation.done) break;
    await delay(POLL_INTERVAL_MS, signal);
    operation = await ai.operations.getVideosOperation({ operation });
  }

  if (!operation.done) {
    throw new Error('Veo video generation timed out after 10 minutes');
  }

  const generatedVideo = operation.response?.generatedVideos?.[0];
  if (!generatedVideo?.video) {
    throw new Error('Veo operation completed but no video returned');
  }

  const video = generatedVideo.video;

  if (video.videoBytes) {
    const bytes = typeof video.videoBytes === 'string'
      ? Uint8Array.from(atob(video.videoBytes), c => c.charCodeAt(0))
      : video.videoBytes;
    return new Blob([bytes], { type: 'video/mp4' });
  }

  if (video.uri) {
    const separator = video.uri.includes('?') ? '&' : '?';
    const authUrl = `${video.uri}${separator}key=${apiKey}`;
    const resp = await fetch(authUrl, { signal });
    if (!resp.ok) throw new Error(`Failed to download Veo video: ${resp.status}`);
    return resp.blob();
  }

  throw new Error('Veo operation completed but no video data found');
}

// ─── Stitch Video Clips Locally ──────────────────────────
//
// Takes multiple video blobs and stitches them sequentially using
// Canvas + MediaRecorder. Each clip is drawn frame-by-frame via <video> element.

async function stitchVideoClips(
  clips: Blob[],
  config: VideoCompilationConfig,
  onProgress: (pct: number) => void,
  signal?: AbortSignal,
): Promise<Blob> {
  if (clips.length === 1) return clips[0];

  const canvas = document.createElement('canvas');
  canvas.width = config.width;
  canvas.height = config.height;
  const ctx = canvas.getContext('2d')!;

  const stream = canvas.captureStream(config.fps);
  let mimeType = 'video/webm;codecs=vp9';
  if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm;codecs=vp8';
  if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';

  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 8_000_000 });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
  recorder.start(100);

  const frameInterval = 1000 / config.fps;

  for (let i = 0; i < clips.length; i++) {
    if (signal?.aborted) { recorder.stop(); throw new Error('Aborted'); }

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.src = URL.createObjectURL(clips[i]);

    // Wait for video metadata to load
    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error(`Failed to load clip ${i + 1}`));
    });

    await video.play();

    // Draw frames until video ends
    while (!video.ended && !video.paused) {
      if (signal?.aborted) { recorder.stop(); video.pause(); throw new Error('Aborted'); }
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const scale = Math.min(canvas.width / video.videoWidth, canvas.height / video.videoHeight);
      const w = video.videoWidth * scale;
      const h = video.videoHeight * scale;
      ctx.drawImage(video, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      await new Promise(r => setTimeout(r, frameInterval));
    }

    video.pause();
    URL.revokeObjectURL(video.src);
    onProgress(Math.round(((i + 1) / clips.length) * 100));
  }

  recorder.stop();
  await new Promise<void>((resolve) => { recorder.onstop = () => resolve(); });
  return new Blob(chunks, { type: mimeType });
}

// ─── AI Video Generation from Slot Images + Per-Image Prompts ─────
//
// FLOW: For each of the 6 screen images:
//   1. Convert image to base64
//   2. Send to Veo: predictLongRunning with image + animation prompt
//   3. Poll until the 8-second clip is ready
//   4. Download the clip
// Then stitch all clips into one video locally using Canvas + MediaRecorder.

/**
 * Generate a video from slot screen images using Veo AI.
 * Each slot gets its own Veo clip (image-to-video), then clips are stitched.
 */
export async function generateAIVideo(
  slotImages: Record<SlotScreenId, VideoSlotImage>,
  config: VideoCompilationConfig,
  onProgress: (pct: number) => void,
  signal?: AbortSignal,
  onScreenStatus?: (screenId: SlotScreenId, status: string) => void,
): Promise<Blob> {
  // Resolve API key — only required for direct (fallback) path
  const useProxy = shouldUseProxy();
  let apiKey: string | null = null;
  if (!useProxy) {
    const apiKeys = useSlotAssetGeneratorStore.getState().apiKeys;
    apiKey = resolveApiKey('google', apiKeys.google);
    if (!apiKey) {
      throw new Error('Google API key is required for Veo video generation. Set GOOGLE_API_KEY or add key in API settings.');
    }
  }

  // Collect slots that have images
  const activeSlots = SLOT_SCREEN_ORDER.filter(id => slotImages[id]?.imageUrl);
  if (activeSlots.length === 0) throw new Error('No images provided for video generation.');

  onProgress(2);

  // Determine aspect ratio from config
  const aspectRatio: '16:9' | '9:16' = config.resolutionPreset === '16:9' ? '16:9' : '9:16';

  // Convert all images to base64 upfront
  const imageData: Array<{ screenId: SlotScreenId; base64: string; mimeType: string }> = [];
  for (const id of activeSlots) {
    if (signal?.aborted) throw new Error('Aborted');
    const url = slotImages[id].imageUrl!;
    const blob = await fetch(url).then(r => r.blob());
    const base64 = await blobToBase64(blob);
    imageData.push({ screenId: id, base64, mimeType: blob.type || 'image/png' });
  }

  onProgress(5);

  // Generate one Veo clip per screen (sequential to avoid rate limits)
  const generationWeight = 85; // 5% + 85% for generation + 10% for stitching
  const clips: Blob[] = [];

  for (let i = 0; i < imageData.length; i++) {
    if (signal?.aborted) throw new Error('Aborted');
    const { screenId, base64, mimeType } = imageData[i];
    const slot = slotImages[screenId];
    const screenName = DEFAULT_SCREEN_CONFIGS[screenId]?.name ?? screenId;

    // Build animation prompt for this screen
    const animPrompt = slot.animationPrompt?.trim();
    const animSystemPrompt = slot.animationSystemPrompt?.trim();

    let prompt: string;
    if (animPrompt) {
      if (animSystemPrompt) {
        // Reasoning step: use Gemini to refine prompt with system context
        onScreenStatus?.(screenId, `Refining prompt for ${screenName}...`);
        try {
          const { refinedPrompt } = await refineAnimationPrompt(
            screenId, animPrompt, animSystemPrompt, screenName, signal,
          );
          prompt = refinedPrompt || `${animPrompt}. This is a ${screenName} screen from a slot machine game.`;
        } catch (err) {
          console.warn(`[VideoCompiler] Reasoning failed for ${screenName}, using raw prompt:`, err);
          prompt = `${animPrompt}. This is a ${screenName} screen from a slot machine game.`;
        }
      } else {
        prompt = `${animPrompt}. This is a ${screenName} screen from a slot machine game.`;
      }
    } else {
      prompt = `Animate this ${screenName} slot game screen with smooth, cinematic motion. Add subtle particle effects and glowing highlights.`;
    }

    onScreenStatus?.(screenId, `Generating video for ${screenName}...`);

    const clipProgressBase = 5 + (i / imageData.length) * generationWeight;
    const clipProgressEnd = 5 + ((i + 1) / imageData.length) * generationWeight;

    onProgress(Math.round(clipProgressBase));

    try {
      const clip = await generateVeoClip(apiKey, prompt, base64, mimeType, aspectRatio, signal);
      clips.push(clip);
    } catch (err) {
      // If a single clip fails, log and skip it (other clips may succeed)
      console.warn(`[VideoCompiler] Clip ${i + 1} (${screenName}) failed:`, err);
      throw err; // For now, fail fast. Could add fallback later.
    }

    onProgress(Math.round(clipProgressEnd));
  }

  if (clips.length === 0) throw new Error('No video clips were generated.');

  // If only one clip, return it directly
  if (clips.length === 1) {
    onProgress(100);
    return clips[0];
  }

  // Stitch clips together
  onProgress(92);
  const final = await stitchVideoClips(clips, config, (pct) => {
    onProgress(92 + Math.round(pct * 0.08));
  }, signal);

  onProgress(100);
  return final;
}

// ─── Public API ───────────────────────────────────────────

export async function compileScreenVideo(
  screenImages: Record<string, string>,
  config: VideoCompilationConfig,
  onProgress: (pct: number) => void,
  signal?: AbortSignal
): Promise<Blob> {
  if (config.videoModel === 'veo') {
    // Convert to VideoSlotImage format for the Veo path
    const slotImages: Record<string, VideoSlotImage> = {};
    for (const [screenId, imageUrl] of Object.entries(screenImages)) {
      slotImages[screenId] = {
        screenId: screenId as SlotScreenId,
        imageUrl,
        source: 'generated',
        animationPrompt: '',
      };
    }
    return generateAIVideo(
      slotImages as Record<SlotScreenId, VideoSlotImage>,
      config,
      onProgress,
      signal,
    );
  }
  return compileLocalWebM(screenImages, config, onProgress, signal);
}
